/**
 * CRM ↔ PortaBilling reconciliation review queue.
 *
 * Lists pending duplicate / unlinked / missing customers detected by the nightly
 * reconcile job, lets a supervisor preview each (side-by-side records + Porta),
 * and apply the fix (merge / link / import) or dismiss it. The destructive work
 * happens server-side; this is the review surface.
 */
import { escapeHtml, formatDateTimeShort } from '../../src/lib/format.js';
import { openDialog, closeDialog } from '../../src/lib/dialog.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const API = api.routes;
const adminAPI = api;
const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

const KIND_META = {
  merge: { label: 'Duplicate', icon: '🔀', cls: 'recon-merge', action: 'Merge' },
  link: { label: 'Unlinked', icon: '🔗', cls: 'recon-link', action: 'Link' },
  import: { label: 'Missing', icon: '➕', cls: 'recon-import', action: 'Import' },
};

// PortaBilling customer bill_status → human label + colour. Surfacing this on the
// queue lets a supervisor judge whether a "missing" customer is worth importing
// (active = yes; closed/blocked = probably skip).
const BILL_STATUS_META = {
  O: { label: 'Active', color: '#15803d', bg: '#dcfce7' },
  OPEN: { label: 'Active', color: '#15803d', bg: '#dcfce7' },
  ACTIVE: { label: 'Active', color: '#15803d', bg: '#dcfce7' },
  S: { label: 'Suspended', color: '#b45309', bg: '#fef3c7' },
  SUSPENDED: { label: 'Suspended', color: '#b45309', bg: '#fef3c7' },
  B: { label: 'Blocked', color: '#b91c1c', bg: '#fee2e2' },
  BLOCKED: { label: 'Blocked', color: '#b91c1c', bg: '#fee2e2' },
  C: { label: 'Closed', color: '#6b7280', bg: '#f3f4f6' },
  CLOSED: { label: 'Closed', color: '#6b7280', bg: '#f3f4f6' },
};

function _billStatus(evidence) {
  const raw = evidence && evidence.bill_status != null ? String(evidence.bill_status).trim() : '';
  if (!raw) return null;
  return BILL_STATUS_META[raw.toUpperCase()] || { label: raw, color: '#374151', bg: '#e5e7eb' };
}

function statusBadge(evidence) {
  const s = _billStatus(evidence);
  if (!s) return '';
  return `<span class="recon-status" title="PortaBilling account status" style="padding:1px 8px; border-radius:10px; font-size:11px; font-weight:600; color:${s.color}; background:${s.bg};">${escapeHtml(s.label)}</span>`;
}

const PAGE_SIZE = 50;
let _filterKind = '';
let _filterStatus = '';        // bill_status filter: active|suspended|blocked|closed
let _offset = 0;
let _total = 0;
let _rows = [];
const _selected = new Set();    // candidate ids selected for a batch (current page)

function _root() {
  return document.getElementById('crmReconciliationView');
}

async function load() {
  const wrap = document.getElementById('crmReconciliationList');
  if (!wrap) return;
  wrap.innerHTML = '<div class="muted">Loading…</div>';
  try {
    const params = new URLSearchParams();
    if (_filterKind) params.set('kind', _filterKind);
    if (_filterStatus) params.set('bill_status', _filterStatus);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(_offset));
    // no-store so a just-applied/dismissed item can't come back from an HTTP cache.
    const data = await adminAPI.fetch(API.crmReconciliation(params.toString()), { method: 'GET', cache: 'no-store' });
    _rows = (data && data.items) || [];
    _total = data ? (data.total || 0) : 0;
    // Stepped past the end (e.g. the last page emptied after a batch)? Go back.
    if (!_rows.length && _offset > 0 && _total > 0) {
      _offset = Math.max(0, (Math.ceil(_total / PAGE_SIZE) - 1) * PAGE_SIZE);
      return load();
    }
    renderList();
  } catch (err) {
    wrap.innerHTML = `<div class="error">Failed to load reconciliation queue: ${escapeHtml(adminAPI.formatErrorDetail(err))}</div>`;
  }
}

function renderFilters() {
  const bar = document.getElementById('crmReconciliationFilters');
  if (!bar) return;
  const chip = (val, label) =>
    `<button type="button" class="btn ${_filterKind === val ? 'active' : ''}" data-recon-filter="${val}" style="font-size:11px; padding:3px 10px;">${escapeHtml(label)}</button>`;
  const schip = (val, label) =>
    `<button type="button" class="btn ${_filterStatus === val ? 'active' : ''}" data-recon-status-filter="${val}" style="font-size:11px; padding:3px 10px;">${escapeHtml(label)}</button>`;
  const wbreak = '<span style="flex-basis:100%; height:0;"></span>';
  bar.innerHTML = [
    chip('', 'All'),
    chip('merge', '🔀 Duplicates'),
    chip('link', '🔗 Unlinked'),
    chip('import', '➕ Missing'),
    wbreak,
    '<span class="muted" style="font-size:11px; align-self:center;">Status:</span>',
    schip('', 'Any'),
    schip('active', 'Active'),
    schip('suspended', 'Suspended'),
    schip('blocked', 'Blocked'),
    schip('closed', 'Closed'),
  ].join('');
}

function renderList() {
  const wrap = document.getElementById('crmReconciliationList');
  if (!wrap) return;
  if (!_rows.length) {
    wrap.innerHTML = '<div class="muted">Nothing to reconcile — the queue is clear. ✅</div>';
    return;
  }
  const cards = _rows.map((c) => {
    const meta = KIND_META[c.kind] || { label: c.kind, icon: '•', cls: '', action: 'Apply' };
    const ev = c.evidence || {};
    // An "unlinked" link candidate has no resolved Porta target — the CRM record
    // needs a manual search to find/link (or merge into) the right customer.
    const isUnlinked = c.kind === 'link' && !c.porta_customer_id;
    const nrec = (c.support_customer_ids || []).length;
    const recInfo = c.kind === 'import' ? 'new customer' : `${nrec} record${nrec === 1 ? '' : 's'}`;
    const checked = _selected.has(String(c.id)) ? 'checked' : '';

    let identityLine;
    let actionBtn;
    if (isUnlinked) {
      const nm = ev.record_name ? escapeHtml(ev.record_name) : '(no name)';
      const ph = ev.record_phone ? ` · ${escapeHtml(ev.record_phone)}` : '';
      const amb = Array.isArray(ev.ambiguous_ids) && ev.ambiguous_ids.length
        ? ` · ⚠ ${ev.ambiguous_ids.length} possible Porta matches` : '';
      identityLine = `<span class="recon-porta"><strong>${nm}</strong>${ph}</span><span class="recon-meta muted">no Porta link${amb}</span>`;
      actionBtn = `<button type="button" class="btn primary" data-recon-action="link-ui" data-id="${c.id}">🔎 Find &amp; link</button>`;
    } else {
      const sub = c.porta_name ? escapeHtml(c.porta_name) : '';
      const phone = c.porta_phone ? ` · ${escapeHtml(c.porta_phone)}` : '';
      identityLine = `<span class="recon-porta">Porta #${escapeHtml(c.porta_customer_id || '—')} ${sub}${phone}</span>${statusBadge(c.evidence)}`;
      actionBtn = `<button type="button" class="btn primary" data-recon-action="apply" data-id="${c.id}">${escapeHtml(meta.action)}</button>`;
    }

    return `
      <div class="recon-card ${meta.cls}" data-recon-id="${c.id}">
        <div class="recon-head">
          <input type="checkbox" class="recon-check" data-recon-check="${c.id}" ${checked} title="Select for batch" style="margin-right:4px;" />
          <span class="recon-badge">${meta.icon} ${escapeHtml(isUnlinked ? 'Unlinked' : meta.label)}</span>
          ${identityLine}
          <span class="recon-meta muted">${recInfo} · detected ${escapeHtml(formatDateTimeShort(c.detected_at))}</span>
        </div>
        <div class="recon-actions">
          <button type="button" class="btn" data-recon-action="preview" data-id="${c.id}">Preview</button>
          ${actionBtn}
          <button type="button" class="btn ghost" data-recon-action="dismiss" data-id="${c.id}">Dismiss</button>
        </div>
      </div>`;
  }).join('');
  wrap.innerHTML = _renderToolbar() + cards;
}

// When every selected item is the same kind, use that kind's verb
// ("Import selected (3)"); for a mixed selection fall back to "Apply selected".
function _batchApplyLabel(n) {
  if (!n) return 'Apply selected';
  const kinds = Object.keys(_selectedKindCounts());
  const verb = kinds.length === 1 && KIND_META[kinds[0]]
    ? KIND_META[kinds[0]].action
    : 'Apply';
  return `${verb} selected (${n})`;
}

function _renderToolbar() {
  const pageStart = _total ? _offset + 1 : 0;
  const pageEnd = _offset + _rows.length;
  const allChecked = _rows.length > 0 && _rows.every((r) => _selected.has(String(r.id)));
  const n = _selected.size;
  return `
    <div class="recon-toolbar" style="display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:8px;">
      <label style="display:flex; align-items:center; gap:6px; font-size:12px;">
        <input type="checkbox" data-recon-select-all ${allChecked ? 'checked' : ''} /> Select page
      </label>
      <span class="muted" style="font-size:12px;">${n} selected</span>
      <button type="button" class="btn primary" data-recon-action="apply-batch" ${n ? '' : 'disabled'}>${escapeHtml(_batchApplyLabel(n))}</button>
      <button type="button" class="btn ghost" data-recon-action="dismiss-batch" ${n ? '' : 'disabled'}>Dismiss selected</button>
      ${n ? '<button type="button" class="btn" data-recon-action="clear-sel">Clear</button>' : ''}
      <span style="flex:1;"></span>
      <span class="muted" style="font-size:12px;">${pageStart}–${pageEnd} of ${_total}</span>
      <button type="button" class="btn" data-recon-page="prev" ${_offset <= 0 ? 'disabled' : ''}>‹ Prev</button>
      <button type="button" class="btn" data-recon-page="next" ${pageEnd >= _total ? 'disabled' : ''}>Next ›</button>
    </div>`;
}

// Drop a resolved item from the in-memory list and repaint immediately, so the
// card disappears the moment the action succeeds — independent of the reload.
function _removeRow(id) {
  const before = _rows.length;
  _rows = _rows.filter((r) => String(r.id) !== String(id));
  _selected.delete(String(id));
  if (_rows.length !== before) renderList();
}

function _fmtCounts(cc) {
  if (!cc) return '';
  return Object.entries(cc).filter(([, n]) => n).map(([k, n]) => `${n} ${k}`).join(', ') || 'no records';
}

async function preview(id) {
  const dlg = document.getElementById('crmReconciliationDialog');
  const body = document.getElementById('crmReconciliationDialogBody');
  if (!dlg || !body) return;
  body.innerHTML = '<div class="muted">Loading preview…</div>';
  openDialog(dlg);
  try {
    const pv = await adminAPI.fetch(API.crmReconciliationPreview(id), { method: 'GET' });
    body.innerHTML = renderPreview(pv, id);
  } catch (err) {
    body.innerHTML = `<div class="error">${escapeHtml(adminAPI.formatErrorDetail(err))}</div>`;
  }
}

function renderPreview(pv, id) {
  const meta = KIND_META[pv.kind] || { action: 'Apply' };
  const porta = pv.porta || {};
  const portaBlock = `
    <div class="recon-porta-block">
      <h4>PortaBilling (source of truth)</h4>
      <div>#${escapeHtml(porta.porta_customer_id || '—')} — <strong>${escapeHtml(porta.name || '')}</strong> ${statusBadge(pv.evidence)}</div>
      <div class="muted">${escapeHtml(porta.phone || '')}</div>
    </div>`;

  let recordsBlock = '';
  if (pv.kind === 'import') {
    recordsBlock = '<div class="muted">No CRM record yet — applying will create one from the Porta profile.</div>';
  } else {
    recordsBlock = (pv.records || []).map((r) => {
      if (r.missing) return `<div class="recon-rec missing">Record ${escapeHtml(r.id)} no longer exists.</div>`;
      const surv = r.is_survivor ? '<span class="recon-survivor">★ survivor</span>' : '<span class="muted">absorbed</span>';
      const link = r.porta_customer_id ? `linked #${escapeHtml(r.porta_customer_id)}` : 'unlinked';
      return `
        <div class="recon-rec ${r.is_survivor ? 'is-survivor' : ''}">
          <div class="recon-rec-head"><strong>${escapeHtml(r.display_name || '(no name)')}</strong> ${surv}</div>
          <div class="muted">${escapeHtml(r.primary_phone || '—')} · ${escapeHtml(r.primary_email || '—')} · ${escapeHtml(link)}</div>
          <div class="muted">history ${r.history_score} · ${escapeHtml(_fmtCounts(r.child_counts))}</div>
        </div>`;
    }).join('');
    if (pv.kind === 'merge') {
      recordsBlock = `<p class="muted">All records below merge into the ★ survivor; their tickets, enquiries, installations and notes move over. The others are deleted (no data is lost).</p>${recordsBlock}`;
    }
  }

  return `
    ${portaBlock}
    <div class="recon-records">${recordsBlock}</div>
    <div class="recon-dialog-actions" style="display:flex; gap:8px; margin-top:6px;">
      <button type="button" class="btn primary" data-recon-action="apply" data-id="${id}" data-from-dialog="1">${escapeHtml(meta.action)}</button>
      <button type="button" class="btn ghost" data-recon-action="dismiss" data-id="${id}" data-from-dialog="1">Dismiss</button>
      <button type="button" class="btn" data-recon-action="close">Cancel</button>
    </div>`;
}

async function apply(id) {
  const row = _rows.find((r) => r.id === id);
  const kind = row ? row.kind : '';
  const verb = (KIND_META[kind] && KIND_META[kind].action) || 'apply';
  const warn = kind === 'merge'
    ? 'Merge these records? The duplicates are deleted and their history moves to the survivor. This cannot be undone.'
    : `${verb} this customer?`;
  if (!window.confirm(warn)) return;
  try {
    await adminAPI.fetch(API.crmReconciliationApply(id), { method: 'POST' });
    showFlash(`${verb} complete.`, 'success');
    const dlg = document.getElementById('crmReconciliationDialog');
    if (dlg) closeDialog(dlg);
    _removeRow(id);
    await load();
  } catch (err) {
    showFlash(`Could not ${verb.toLowerCase()}: ${adminAPI.formatErrorDetail(err)}`, 'error');
  }
}

async function dismiss(id) {
  const note = window.prompt('Dismiss this item — optional note:', '');
  if (note === null) return; // cancelled
  try {
    await adminAPI.fetch(API.crmReconciliationDismiss(id), {
      method: 'POST',
      body: JSON.stringify({ note: note || null }),
    });
    showFlash('Dismissed.', 'success');
    const dlg = document.getElementById('crmReconciliationDialog');
    if (dlg) closeDialog(dlg);
    _removeRow(id);
    await load();
  } catch (err) {
    showFlash(`Could not dismiss: ${adminAPI.formatErrorDetail(err)}`, 'error');
  }
}

// ---- Manual link / merge flow (unlinked candidates) --------------------

let _linkCand = null;

function openLinkUI(id) {
  const cand = _rows.find((r) => String(r.id) === String(id));
  if (!cand) return;
  _linkCand = cand;
  const dlg = document.getElementById('crmReconciliationDialog');
  const body = document.getElementById('crmReconciliationDialogBody');
  if (!dlg || !body) return;
  body.innerHTML = _renderLinkUI(cand);
  openDialog(dlg);
  const ev = cand.evidence || {};
  runPortaSearch(ev.record_phone || ev.record_name || '');
}

function _renderLinkUI(cand) {
  const ev = cand.evidence || {};
  const nm = ev.record_name ? escapeHtml(ev.record_name) : '(no name)';
  const ph = ev.record_phone ? escapeHtml(ev.record_phone) : '—';
  const ambNote = Array.isArray(ev.ambiguous_ids) && ev.ambiguous_ids.length
    ? `<div class="muted" style="font-size:12px;">Possible Porta ids from this customer's tickets/phone: ${ev.ambiguous_ids.map(escapeHtml).join(', ')}</div>`
    : '';
  const prefill = ev.record_phone || ev.record_name || '';
  return `
    <div class="recon-porta-block">
      <h4>Unlinked CRM customer</h4>
      <div><strong>${nm}</strong> · ${ph}</div>
      ${ambNote}
    </div>
    <div style="margin-top:10px;">
      <h4 style="margin:0 0 6px;">Link to a PortaBilling customer</h4>
      <input type="text" id="reconPortaSearchInput" placeholder="Search Porta by phone or name…" value="${escapeHtml(prefill)}" style="width:100%; padding:6px 8px;" />
      <div id="reconPortaResults" class="muted" style="margin-top:6px;">Type to search…</div>
    </div>
    <div style="margin-top:14px; border-top:1px solid var(--border,#e5e7eb); padding-top:10px;">
      <h4 style="margin:0 0 6px;">…or merge into an existing CRM customer</h4>
      <input type="text" id="reconMergeSearchInput" placeholder="Search CRM customers by name/phone…" style="width:100%; padding:6px 8px;" />
      <div id="reconMergeResults" class="muted" style="margin-top:6px;">Type to search…</div>
    </div>
    <div class="recon-dialog-actions" style="display:flex; gap:8px; margin-top:12px;">
      <button type="button" class="btn ghost" data-recon-action="dismiss" data-id="${cand.id}" data-from-dialog="1">Dismiss</button>
      <button type="button" class="btn" data-recon-action="close">Cancel</button>
    </div>`;
}

async function runPortaSearch(q) {
  const box = document.getElementById('reconPortaResults');
  if (!box || !_linkCand) return;
  const query = (q || '').trim();
  if (query.length < 3) { box.textContent = 'Type at least 3 characters…'; return; }
  box.textContent = 'Searching PortaBilling…';
  // Single free-text query — backend partial-matches name (covers numbers in name).
  const params = new URLSearchParams();
  params.set('q', query);
  try {
    const res = await adminAPI.fetch(API.crmReconciliationPortaSearch(params.toString()), { method: 'GET' });
    const items = (res && res.items) || [];
    if (!items.length) { box.innerHTML = '<span class="muted">No active Porta customers found.</span>'; return; }
    box.innerHTML = items.map((it) => `
      <div style="display:flex; align-items:center; gap:8px; padding:4px 0; border-bottom:1px solid var(--border,#eee);">
        <div style="flex:1;"><strong>#${escapeHtml(it.i_customer)}</strong> ${escapeHtml(it.name || '')} <span class="muted">${escapeHtml(it.phone || '')}</span></div>
        <button type="button" class="btn primary" data-recon-action="do-link" data-id="${_linkCand.id}" data-porta="${escapeHtml(it.i_customer)}">Link</button>
      </div>`).join('');
  } catch (err) {
    box.innerHTML = `<span class="error">${escapeHtml(adminAPI.formatErrorDetail(err))}</span>`;
  }
}

async function runMergeSearch(q) {
  const box = document.getElementById('reconMergeResults');
  if (!box || !_linkCand) return;
  const query = (q || '').trim();
  if (query.length < 2) { box.textContent = 'Type at least 2 characters…'; return; }
  box.textContent = 'Searching CRM customers…';
  const params = new URLSearchParams();
  params.set('search', query);
  params.set('limit', '8');
  try {
    const res = await adminAPI.fetch(API.crmCustomers(params.toString()), { method: 'GET' });
    const items = (res && res.items) || [];
    const orphanId = (_linkCand.support_customer_ids || [])[0];
    const list = items.filter((it) => String(it.id) !== String(orphanId));
    if (!list.length) { box.innerHTML = '<span class="muted">No other CRM customers found.</span>'; return; }
    box.innerHTML = list.map((it) => `
      <div style="display:flex; align-items:center; gap:8px; padding:4px 0; border-bottom:1px solid var(--border,#eee);">
        <div style="flex:1;"><strong>${escapeHtml(it.display_name || '(no name)')}</strong> <span class="muted">${escapeHtml(it.primary_phone || '')}${it.porta_customer_id ? ` · Porta #${escapeHtml(it.porta_customer_id)}` : ''}</span></div>
        <button type="button" class="btn" data-recon-action="do-merge" data-id="${_linkCand.id}" data-target="${escapeHtml(it.id)}">Merge here</button>
      </div>`).join('');
  } catch (err) {
    box.innerHTML = `<span class="error">${escapeHtml(adminAPI.formatErrorDetail(err))}</span>`;
  }
}

async function doLink(id, portaId) {
  if (!portaId) return;
  try {
    await adminAPI.fetch(API.crmReconciliationLink(id), {
      method: 'POST', body: JSON.stringify({ porta_customer_id: String(portaId) }),
    });
    showFlash(`Linked to Porta #${portaId}.`, 'success');
    const dlg = document.getElementById('crmReconciliationDialog'); if (dlg) closeDialog(dlg);
    _removeRow(id); await load();
  } catch (err) {
    showFlash(`Could not link: ${adminAPI.formatErrorDetail(err)}`, 'error');
  }
}

async function doMerge(id, targetId) {
  if (!targetId) return;
  if (!window.confirm('Merge this unlinked customer into the selected CRM customer? Their tickets/history move over and the duplicate is deleted. This cannot be undone.')) return;
  try {
    await adminAPI.fetch(API.crmReconciliationMergeInto(id), {
      method: 'POST', body: JSON.stringify({ target_support_customer_id: String(targetId) }),
    });
    showFlash('Merged.', 'success');
    const dlg = document.getElementById('crmReconciliationDialog'); if (dlg) closeDialog(dlg);
    _removeRow(id); await load();
  } catch (err) {
    showFlash(`Could not merge: ${adminAPI.formatErrorDetail(err)}`, 'error');
  }
}

function _selectedKindCounts() {
  const counts = {};
  _rows.forEach((r) => {
    if (_selected.has(String(r.id))) counts[r.kind] = (counts[r.kind] || 0) + 1;
  });
  return counts;
}

function _reportBatch(res, verb) {
  const applied = (res && res.applied) || 0;
  const failed = (res && res.failed) || 0;
  if (failed) {
    const firstErr = ((res && res.results) || []).find((r) => !r.ok);
    const hint = firstErr && firstErr.error ? ` (e.g. ${firstErr.error})` : '';
    showFlash(`${applied} ${verb}, ${failed} failed${hint}.`, 'warning', 9000);
  } else {
    showFlash(`${applied} ${verb}.`, 'success');
  }
}

// Drop the ids the server confirmed from the selection; keep failures selected
// so they can be retried/inspected.
function _pruneSelected(res) {
  ((res && res.results) || []).forEach((r) => {
    if (r.ok) _selected.delete(String(r.id));
  });
}

async function applyBatch() {
  const ids = Array.from(_selected);
  if (!ids.length) return;
  const counts = _selectedKindCounts();
  const parts = Object.entries(counts)
    .map(([k, n]) => `${n} ${(KIND_META[k] && KIND_META[k].label.toLowerCase()) || k}`);
  const hasMerge = (counts.merge || 0) > 0;
  const warn = `Apply ${ids.length} item(s) — ${parts.join(', ')}?`
    + (hasMerge ? '\n\nMerges permanently delete duplicate records (their history moves to the survivor). This cannot be undone.' : '');
  if (!window.confirm(warn)) return;
  try {
    const res = await adminAPI.fetch(API.crmReconciliationApplyBatch, {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
    _reportBatch(res, 'applied');
    _pruneSelected(res);
    await load();
  } catch (err) {
    showFlash(`Batch apply failed: ${adminAPI.formatErrorDetail(err)}`, 'error');
  }
}

async function dismissBatch() {
  const ids = Array.from(_selected);
  if (!ids.length) return;
  const note = window.prompt(`Dismiss ${ids.length} item(s) — optional note:`, '');
  if (note === null) return; // cancelled
  try {
    const res = await adminAPI.fetch(API.crmReconciliationDismissBatch, {
      method: 'POST',
      body: JSON.stringify({ ids, note: note || null }),
    });
    _reportBatch(res, 'dismissed');
    _pruneSelected(res);
    await load();
  } catch (err) {
    showFlash(`Batch dismiss failed: ${adminAPI.formatErrorDetail(err)}`, 'error');
  }
}

function _goToPage(dir) {
  if (dir === 'prev') _offset = Math.max(0, _offset - PAGE_SIZE);
  else if (dir === 'next') _offset += PAGE_SIZE;
  _selected.clear(); // selection is page-scoped
  load();
}

let _wired = false;
function wire() {
  if (_wired) return;
  const root = _root();
  if (!root) return;
  _wired = true;
  renderFilters();

  const filters = document.getElementById('crmReconciliationFilters');
  if (filters) {
    filters.addEventListener('click', (e) => {
      const kindBtn = e.target.closest('[data-recon-filter]');
      if (kindBtn) {
        _filterKind = kindBtn.getAttribute('data-recon-filter') || '';
        _offset = 0;
        _selected.clear();
        renderFilters();
        return void load();
      }
      const statusBtn = e.target.closest('[data-recon-status-filter]');
      if (statusBtn) {
        _filterStatus = statusBtn.getAttribute('data-recon-status-filter') || '';
        _offset = 0;
        _selected.clear();
        renderFilters();
        return void load();
      }
    });
  }

  const onAction = (e) => {
    const pageBtn = e.target.closest('[data-recon-page]');
    if (pageBtn) return void _goToPage(pageBtn.getAttribute('data-recon-page'));
    const btn = e.target.closest('[data-recon-action]');
    if (!btn) return;
    const action = btn.getAttribute('data-recon-action');
    const id = btn.getAttribute('data-id');
    if (action === 'preview') return void preview(id);
    if (action === 'apply') return void apply(id);
    if (action === 'dismiss') return void dismiss(id);
    if (action === 'apply-batch') return void applyBatch();
    if (action === 'dismiss-batch') return void dismissBatch();
    if (action === 'clear-sel') { _selected.clear(); return void renderList(); }
    if (action === 'link-ui') return void openLinkUI(id);
    if (action === 'do-link') return void doLink(id, btn.getAttribute('data-porta'));
    if (action === 'do-merge') return void doMerge(id, btn.getAttribute('data-target'));
    if (action === 'close') {
      const dlg = document.getElementById('crmReconciliationDialog');
      if (dlg) closeDialog(dlg);
    }
  };

  // Debounced search inside the link/merge modal.
  let _portaTimer = null;
  let _mergeTimer = null;
  const onDialogInput = (e) => {
    const porta = e.target.closest('#reconPortaSearchInput');
    if (porta) {
      clearTimeout(_portaTimer);
      const v = porta.value;
      // Wait for a typing pause before hitting PortaBilling.
      _portaTimer = setTimeout(() => runPortaSearch(v), 700);
      return;
    }
    const merge = e.target.closest('#reconMergeSearchInput');
    if (merge) {
      clearTimeout(_mergeTimer);
      const v = merge.value;
      _mergeTimer = setTimeout(() => runMergeSearch(v), 350);
    }
  };

  // Checkbox selection (change events): per-card + select-all-on-page.
  const onChange = (e) => {
    const cb = e.target.closest('[data-recon-check]');
    if (cb) {
      const id = String(cb.getAttribute('data-recon-check'));
      if (cb.checked) _selected.add(id); else _selected.delete(id);
      return void renderList();
    }
    const all = e.target.closest('[data-recon-select-all]');
    if (all) {
      if (all.checked) _rows.forEach((r) => _selected.add(String(r.id)));
      else _rows.forEach((r) => _selected.delete(String(r.id)));
      return void renderList();
    }
  };

  const list = document.getElementById('crmReconciliationList');
  if (list) {
    list.addEventListener('click', onAction);
    list.addEventListener('change', onChange);
  }
  const dlgBody = document.getElementById('crmReconciliationDialogBody');
  if (dlgBody) {
    dlgBody.addEventListener('click', onAction);
    dlgBody.addEventListener('input', onDialogInput);
  }
  const refreshBtn = document.getElementById('crmReconciliationRefresh');
  if (refreshBtn) refreshBtn.addEventListener('click', () => load());
}

function mount() {
  wire();
  load();
}

export default { mount, load };
